import type { ComputedRef, Ref } from 'vue'
import type {
  DateRequestErrors,
  PossibleInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import type { DateRequestFlow } from '~/composables/useDateRequestFlow'
import type { DateRequestFormModel } from '~/composables/useDateRequestFormState'
import { useFormValidation } from '~/composables/useFormValidation'
import { useDateRequest } from '~/composables/useDateRequest'
import {
  getDateRequestApiErrorData,
  mapDateRequestApiErrors,
} from '~/utils/mapDateRequestApiErrors'

type RulesMap = Record<string, string>

type SubmissionMode = 'private' | 'applicable' | 'listing'

interface SubmissionEmit {
  // Kept as separate overloads (not unified via a event-name union) because
  // Vue's defineEmits generates one overload per key — a unified signature
  // here is no longer assignable from that intersection type.
  (event: 'on-complete-request', dateRequestId?: string): void
  // eslint-disable-next-line @typescript-eslint/unified-signatures
  (event: 'on-complete-private-request', dateRequestId?: string): void
  (event: 'onCloseDateRequest'): void
}

interface UseDateRequestSubmissionOptions {
  emit: SubmissionEmit
}

export interface DateRequestSubmission {
  errors: Ref<DateRequestErrors>
  invalidKeys: Ref<PossibleInvalidKeys[]>
  clearErrors: (field?: string) => void
  rules: ComputedRef<RulesMap>
  submit: () => Promise<void>
  loading: Ref<boolean>
  buttonText: ComputedRef<string>
  canUserBeContactedChecked: Ref<boolean>
  isContactConfirmationChecked: Ref<boolean>
  handleClickSeeMore: () => void
  submitError: Ref<string | null>
}

/**
 * Owns validation rules (as a computed derived from `flow`), a single
 * `submit()` entry that branches on the active flow, and the
 * confirmation-checkbox state for the private path.
 */
export const useDateRequestSubmission = (
  form: Ref<DateRequestFormModel>,
  flow: DateRequestFlow,
  options: UseDateRequestSubmissionOptions
): DateRequestSubmission => {
  const { emit } = options

  const { t } = useI18n()
  const router = useRouter()
  const localePath = useLocalePath()
  const eventData = useEventData()
  const { requestEventDates: requestEventDatesComposable } = useDateRequest()

  const loading = ref(false)
  const submitError = ref<string | null>(null)
  const canUserBeContactedChecked = ref(false)
  const isContactConfirmationChecked = ref(true)

  const mode = computed<SubmissionMode>(() => {
    if (flow.isPrivateEventRequest.value) {
      return 'private'
    }
    if (flow.isDateRequestApplicable.value) {
      return 'applicable'
    }
    return 'listing'
  })

  const baseRules: RulesMap = {
    email: 'required|email',
    name: 'required|min:3',
    surname: 'required|min:3',
    phone: 'required|phoneNumber',
  }

  const dateRequestTicketsRule = computed(
    () =>
      `required|numeric|min:${eventGetters.getMinDateRequestTickets(
        eventData.value
      )}|max:${eventGetters.getMaxDateRequestTickets(eventData.value)}`
  )

  const rules = computed<RulesMap>(() => {
    if (mode.value === 'private') {
      return {
        ...baseRules,
        numberOfTickets: 'required',
        dates: 'required|array',
      }
    }
    if (mode.value === 'applicable') {
      return {
        ...baseRules,
        phone: 'phoneNumber',
        numberOfTickets: dateRequestTicketsRule.value,
        dates: 'required|array',
        dateType: 'required|in:PUBLIC,PRIVATE',
      }
    }
    return {
      ...baseRules,
      numberOfTickets: dateRequestTicketsRule.value,
    }
  })

  const {
    errors,
    clearErrors: clearValidationErrors,
    validate,
    invalidKeys,
  } = useFormValidation(form, rules) as {
    errors: Ref<DateRequestErrors>
    invalidKeys: Ref<PossibleInvalidKeys[]>
    clearErrors: (field?: string) => void
    validate: (key?: string) => Promise<void>
  }

  const clearErrors = (field?: string) => {
    if (!field) {
      clearValidationErrors()
      return
    }

    // dateSlots lives under a different key than 'dates', so useFormValidation's
    // clearErrors('dates') early-returns when only slot errors exist — clear both
    // the invalidKeys entry and dateSlots here.
    if (field === 'dates') {
      const datesIndex = invalidKeys.value.indexOf('dates')
      if (datesIndex !== -1) {
        invalidKeys.value.splice(datesIndex, 1)
      }
      if (errors.value) {
        const { dates: _dates, dateSlots: _slots, ...rest } = errors.value
        errors.value = rest
      }
      return
    }

    clearValidationErrors(field)
  }

  const applySubmitApiError = (error: unknown) => {
    clearErrors()

    const apiErrorData = getDateRequestApiErrorData(error)
    const mapped = mapDateRequestApiErrors(apiErrorData)

    if (mapped.invalidKeys.length > 0) {
      errors.value = mapped.fieldErrors
      invalidKeys.value = mapped.invalidKeys
    }

    submitError.value =
      mapped.submitError ??
      (mapped.invalidKeys.length === 0
        ? (apiErrorData?.message ?? t('general.error'))
        : null)
  }

  // Clear validation state and reset the confirmation checkbox whenever we
  // flip between private and standard flows.
  watch(
    () => flow.isPrivateEventRequest.value,
    () => {
      clearErrors()
      submitError.value = null
      isContactConfirmationChecked.value = true
    }
  )

  watch(canUserBeContactedChecked, (checked) => {
    isContactConfirmationChecked.value = checked
  })

  const hasBlockingErrors = () =>
    errors.value !== null && Object.keys(errors.value ?? {}).length > 0

  const submitPrivate = async () => {
    loading.value = true
    submitError.value = null
    isContactConfirmationChecked.value = canUserBeContactedChecked.value
    await validate()

    if (hasBlockingErrors()) {
      loading.value = false
      return
    }
    if (!canUserBeContactedChecked.value) {
      loading.value = false
      return
    }

    const eventDescriptionId =
      flow.isFromEventPage.value && eventData.value
        ? eventGetters.getId(eventData.value)
        : undefined

    try {
      await requestEventDatesComposable({
        eventDescriptionId,
        form,
        isPrivateEvent: true,
        onSuccess: (dateRequestId?: string) => {
          emit('on-complete-private-request', dateRequestId)
        },
        onError: (error: unknown) => {
          applySubmitApiError(error)
        },
      })
    } catch {
      // submitError is set in onError
    } finally {
      loading.value = false
    }
  }

  const submitApplicable = async () => {
    if (!flow.isDateRequestApplicable.value) {
      return
    }
    if (flow.isPartySizeInPurchasePrivateGap.value) {
      return
    }
    submitError.value = null

    isContactConfirmationChecked.value = canUserBeContactedChecked.value

    await validate()
    if (form.value.dates?.length === 0) {
      loading.value = false
      return
    }
    if (hasBlockingErrors()) {
      loading.value = false
      return
    }
    if (!canUserBeContactedChecked.value) {
      loading.value = false
      return
    }

    if (form.value.dateType !== 'PUBLIC' && form.value.dateType !== 'PRIVATE') {
      loading.value = false
      return
    }

    try {
      await requestEventDatesComposable({
        eventDescriptionId: eventGetters.getId(eventData.value),
        form,
        isPrivateEvent: false,
        onSuccess: (dateRequestId?: string) => {
          emit('on-complete-request', dateRequestId)
        },
        onError: (error: unknown) => {
          applySubmitApiError(error)
        },
      })
    } catch {
      // submitError is set in onError
    } finally {
      loading.value = false
    }
  }

  const handleClickSeeMore = () => {
    emit('onCloseDateRequest')
    router.push({ path: localePath('search') })
  }

  const submit = async () => {
    if (mode.value === 'private') {
      await submitPrivate()
      return
    }
    if (mode.value === 'applicable') {
      await submitApplicable()
      return
    }
    handleClickSeeMore()
  }

  const buttonText = computed(() => {
    if (
      flow.isDateRequestApplicable.value ||
      flow.isPrivateEventRequest.value
    ) {
      return t('subscribeToEventUpdatesModal.firstStep.requestDatesButtonLabel')
    }
    return t('dateRequest.eventSuggestions.seeMoreButton')
  })

  return {
    errors,
    invalidKeys,
    clearErrors,
    rules,
    submit,
    loading,
    buttonText,
    canUserBeContactedChecked,
    isContactConfirmationChecked,
    handleClickSeeMore,
    submitError,
  }
}
